import axios, { AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import accessToken from '|/helpers/access-token';
import { datadogRum } from '@datadog/browser-rum';

interface Props {
  experimentName: string;
  identifier: string;
  apiTimeout?: number;
  cacheMinutes?: number;
}

type GetAssignedBucketProps = Omit<Props, 'identifier'> &
  Partial<Pick<Props, 'identifier'>>;

interface Response {
  content: {
    experimentName: string;
    bucketKey: string;
  };
}

interface Request {
  experimentName: string;
  identifier: string;
}

const GetAssignedBucket = async ({
  experimentName,
  identifier: identifierOverride,
  apiTimeout = 2000,
  cacheMinutes = 15
}: GetAssignedBucketProps) => {
  const { user } = accessToken();

  const identifier = identifierOverride ?? user.user_id;
  const cachedCookie = Cookies.get(experimentName);
  const hasCachedCookie = !!cachedCookie;

  if (hasCachedCookie) {
    return {
      assignedBucket: cachedCookie,
      experimentName
    };
  }

  if (!user.user_id) {
    return {
      assignedBucket: 'control',
      experimentName,
      hasErrors: false
    };
  }

  return axios
    .post<Request, AxiosResponse<Response>>(
      `${process.env.SPLITTEST_API_URL}/api/v2/buckets`,
      {
        experimentName,
        identifier
      },
      { timeout: apiTimeout }
    )
    .then(({ data: { content: { bucketKey = null } = {} } = {} }) => {
      if (cacheMinutes !== 0 && bucketKey) {
        Cookies.set(experimentName, bucketKey, {
          expires: new Date(new Date().getTime() + cacheMinutes * 60 * 1000)
        });
      }

      const experimentObject = {
        assignedBucket: bucketKey,
        experimentName,
        hasErrors: false
      };
      datadogRum.addAction('user-bucketed', {
        experimentName,
        bucketKey
      });

      return experimentObject;
    })
    .catch(() => ({
      assignedBucket: null,
      experimentName,
      hasErrors: true
    }));
};

export default GetAssignedBucket;
